import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Check, Clock, AlertCircle, DollarSign, Calendar, User, FileText, X } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface Notification {
  id: string;
  type: string;
  content: string;
  is_read: boolean;
  created_at: string;
  related_id: string | null;
}

const NotificationsDropdown: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Mock notifications for when Supabase is not configured
  const mockNotifications: Notification[] = [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen && user) {
      fetchNotifications();
    }
  }, [isOpen, user]);

  const fetchNotifications = async () => {
    console.log('🔔 Fetching notifications...');
    
    // If Supabase is not configured, return empty data
    if (!isSupabaseConfigured || !supabase) {
      console.error('Supabase not configured - cannot fetch notifications');
      setNotifications([]);
      setLoading(false);
      return;
    }

    if (!user) {
      console.log('❌ No user found for notifications');
      setNotifications([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      console.log('🔍 Querying Supabase for unread notifications...');
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        // Ignorer silencieusement les erreurs 400/404/406 (table n'existe pas)
        const errorCode = error.code || '';
        const httpStatus = (error as any)?.status || '';
        const isTableMissing = errorCode === 'PGRST400' || 
                               errorCode === 'PGRST406' || 
                               errorCode === '42P01' ||
                               httpStatus === 400 ||
                               httpStatus === 404 ||
                               httpStatus === 406;
        
        if (isTableMissing) {
          // Table n'existe pas - c'est normal, on continue avec une liste vide
          setNotifications([]);
          setLoading(false);
          return;
        }
        
        console.error('❌ Error fetching notifications:', error);
        setNotifications([]);
        // Ne pas afficher de toast pour les erreurs de table manquante
        if (!isTableMissing) {
          toast.error('Failed to load notifications');
        }
        return;
      }

      console.log('✅ Notifications loaded:', data?.length || 0);
      setNotifications(data || []);
    } catch (error) {
      // Ignorer silencieusement les erreurs de table manquante
      const errorMsg = String(error || '');
      const isTableMissing = errorMsg.toLowerCase().includes('does not exist') || 
                             errorMsg.toLowerCase().includes('relation') ||
                             errorMsg.toLowerCase().includes('undefined table');
      
      if (!isTableMissing) {
        console.error('💥 Error fetching notifications:', error);
        toast.error('Failed to load notifications');
      }
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    console.log('✅ Marking notification as read:', notificationId);
    
    // If Supabase is not configured, return error
    if (!isSupabaseConfigured || !supabase) {
      console.error('Supabase not configured - cannot mark notification as read');
      toast.error('Database not configured');
      return;
    }

    try {
      setMarkingAsRead(notificationId);
      
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) {
        console.error('❌ Error marking notification as read:', error);
        throw error;
      }

      console.log('✅ Notification marked as read successfully');
      
      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification marked as read');
    } catch (error) {
      console.error('💥 Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    } finally {
      setMarkingAsRead(null);
    }
  };

  const markAllAsRead = async () => {
    if (notifications.length === 0) return;
    
    console.log('✅ Marking all notifications as read');
    
    // If Supabase is not configured, return error
    if (!isSupabaseConfigured || !supabase) {
      console.error('Supabase not configured - cannot mark all notifications as read');
      toast.error('Database not configured');
      return;
    }

    try {
      setLoading(true);
      
      const notificationIds = notifications.map(n => n.id);
      
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .in('id', notificationIds);

      if (error) {
        console.error('❌ Error marking all notifications as read:', error);
        throw error;
      }

      console.log('✅ All notifications marked as read successfully');
      setNotifications([]);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('💥 Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'task_due':
      case 'deadline_approaching':
        return <Clock className="text-orange-500" size={16} />;
      case 'invoice_pending':
      case 'payment_received':
        return <DollarSign className="text-green-500" size={16} />;
      case 'client_message':
      case 'new_client':
        return <User className="text-[rgb(var(--brand))]" size={16} />;
      case 'order_update':
      case 'order_completed':
        return <FileText className="text-purple-500" size={16} />;
      case 'system_alert':
      case 'error':
        return <AlertCircle className="text-red-500" size={16} />;
      default:
        return <Bell className="text-[rgb(var(--muted))]" size={16} />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const notificationDate = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - notificationDate.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return notificationDate.toLocaleDateString();
  };

  const unreadCount = notifications.length;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null);

  // Calculate dropdown position with collision detection
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const dropdownWidth = 320; // w-80 = 320px
      const dropdownHeight = 400; // max-h-96 approximate
      const spacing = 8; // mt-2 = 8px
      
      let right = viewportWidth - buttonRect.right;
      let top = buttonRect.bottom + spacing;
      
      // Collision detection: ensure dropdown stays on screen
      if (right + dropdownWidth > viewportWidth) {
        right = Math.max(8, viewportWidth - dropdownWidth - 8);
      }
      if (top + dropdownHeight > viewportHeight) {
        top = Math.max(8, buttonRect.top - dropdownHeight - spacing);
      }
      
      setDropdownPosition({ top, right });
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="group relative p-2 theme-nav-icon hover:theme-hover-button hover:theme-nav-icon-hover rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
        title="Notifications"
      >
        <Bell size={19} className="transition-transform group-hover:scale-105" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-[rgb(var(--fg))] text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown - Portal for proper z-index and collision handling */}
      {isOpen && dropdownPosition && typeof window !== 'undefined' && createPortal(
        <div 
          className="fixed theme-card-bg rounded-lg shadow-lg theme-card-border z-[60] w-72 max-w-[90vw]"
          style={{
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b theme-card-border">
            <h3 className="text-lg font-semibold theme-text-primary">
              Notifications
            </h3>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  disabled={loading}
                  className="text-xs text-[rgb(var(--brand))] dark:text-[rgb(var(--brand))] hover:text-[rgb(var(--brand))] dark:hover:text-[rgb(var(--brand))] font-medium disabled:opacity-50"
                >
                  Mark all as read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="theme-text-secondary hover:theme-text-primary"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <p className="ml-2 theme-text-secondary text-sm">Loading notifications...</p>
              </div>
            ) : notifications.length > 0 ? (
              <div className="divide-y theme-card-border">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="p-4 hover:theme-hover-button transition-colors"
                  >
                    <div className="flex items-start space-x-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm theme-text-primary break-words">
                          {notification.content}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-xs theme-text-secondary">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                          <button
                            onClick={() => markAsRead(notification.id)}
                            disabled={markingAsRead === notification.id}
                            className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-[rgb(var(--brand))] dark:text-[rgb(var(--brand))] rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors disabled:opacity-50"
                          >
                            {markingAsRead === notification.id ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600 mr-1"></div>
                            ) : (
                              <Check size={12} className="mr-1" />
                            )}
                            Mark as read
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Bell className="mx-auto h-8 w-8 theme-text-secondary" />
                <p className="mt-2 text-sm theme-text-secondary">No new notifications</p>
                <p className="text-xs theme-text-tertiary mt-1">
                  You're all caught up!
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t theme-card-border">
              <button
                onClick={() => {
                  setIsOpen(false);
                  // Here you could navigate to a full notifications page
                }}
                className="w-full text-center text-sm text-[rgb(var(--brand))] dark:text-[rgb(var(--brand))] hover:text-[rgb(var(--brand))] dark:hover:text-[rgb(var(--brand))] font-medium"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationsDropdown;